/* RESPONSIBLE TEAM: team-reporting */

import type {
  Cell,
  ChartConfig,
  ChartDataResponse,
  FractionUnit,
  RenderContext,
  TimeInterval,
} from '@/features/reporting-charts/types';
import { isDimensionCell, isDualQueryCell, isGroupedData } from '@/features/reporting-charts/types';
import {
  applyFractionUnit,
  findPeriod,
  formatTimeDimensionLabel,
  formatValue,
  getDisplayNameWithAggregation,
} from '@/features/reporting-charts/transforms/utils';
import { SERIES_COLOR_PALETTE } from '@/features/reporting-charts/constants';
import { getSeriesColor } from '@/features/reporting-charts/transforms/color-registry';

interface DonutDataPoint {
  name: string;
  y: number | null;
  color?: string;
}

/**
 * Transforms ChartDataResponse into Highcharts options for donut charts.
 *
 * Donut charts display grouped data as slices:
 * - Each row represents a slice: [DimensionCell, MetricValue]
 * - The dimension's displayValue becomes the slice name
 * - The metric value becomes the slice size (y)
 */
export function buildDonutOptions(
  data: ChartDataResponse,
  config: ChartConfig,
  renderContext: RenderContext,
): Highcharts.Options {
  const current = findPeriod(data, 'current');

  if (!current || !isGroupedData(current.data) || current.data.length === 0) {
    return emptyOptions();
  }

  const metricMetadata = current.metadata.metricMetadata;
  const firstSeries = config.chartSeries?.[0];
  const fallbackName = metricMetadata[0]?.metricId ?? 'Value';
  const seriesName = getDisplayNameWithAggregation(firstSeries, fallbackName) ?? fallbackName;
  const showDataLabels = config.chartSeries?.[0]?.showDataLabels ?? false;

  const primaryUnit = metricMetadata[0]?.unit ?? 'value';
  const showPercentages = primaryUnit === 'value';

  const isTimeSeries = config.viewBy === 'time';
  const fractionUnit = metricMetadata[0]?.fractionUnit;
  const seriesData = buildDonutData(
    current.data,
    isTimeSeries,
    config.viewByTimeInterval,
    renderContext,
    config.viewBy,
    fractionUnit,
  );

  return {
    chart: { type: 'pie' },
    colors: SERIES_COLOR_PALETTE,
    plotOptions: {
      pie: {
        allowPointSelect: true,
        showInLegend: true,
        innerSize: '60%',
        borderWidth: 0,
        borderRadius: 0,
        dataLabels: {
          enabled: showDataLabels,
          useHTML: true,
          style: { fontWeight: 'normal', textOutline: 'none' },
          formatter: function (this: Highcharts.Point) {
            const formattedValue = formatValue(this.y ?? null, primaryUnit, renderContext);
            if (showPercentages) {
              const percentage = (this as Highcharts.Point & { percentage?: number }).percentage;
              const percentStr =
                typeof percentage === 'number' ? `${Math.round(percentage * 100) / 100}%` : '';
              return `<strong>${percentStr} (${formattedValue})</strong><br/>(${this.name})`;
            }
            return `<strong>${formattedValue}</strong><br/>(${this.name})`;
          },
        },
      },
    },
    series: [
      {
        type: 'pie',
        name: seriesName,
        data: seriesData,
      } as Highcharts.SeriesOptionsType,
    ],
    tooltip: {
      useHTML: true,
      outside: true,
      shared: false,
      backgroundColor: 'var(--neutral-fill)',
      borderColor: 'var(--neutral-border)',
      borderRadius: 10,
      style: {
        color: 'var(--text-on-neutral)',
      },
      formatter: function (this: Highcharts.Point) {
        const formattedValue = formatValue(this.y ?? null, primaryUnit, renderContext);
        if (showPercentages) {
          const percentage = (this as Highcharts.Point & { percentage?: number }).percentage;
          const percentStr =
            typeof percentage === 'number' ? `${Math.round(percentage * 100) / 100}%` : '';
          return `<strong>${percentStr} (${formattedValue})</strong><br/>(${this.name})`;
        }
        return `<strong>${formattedValue}</strong><br/>(${this.name})`;
      },
    },
    legend: { enabled: true },
    credits: { enabled: false },
  };
}

/**
 * Extracts donut data points from grouped data rows.
 * Each row should be [DimensionCell, MetricValue].
 */
function buildDonutData(
  rows: Cell[][],
  isTimeSeries: boolean,
  interval: TimeInterval | undefined,
  renderContext: RenderContext,
  viewBy?: string,
  fractionUnit?: FractionUnit,
): DonutDataPoint[] {
  const dataPoints: DonutDataPoint[] = [];

  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    const [dimensionCell, valueCell] = row;

    if (!isDimensionCell(dimensionCell)) {
      continue;
    }

    const isLastRow = index === rows.length - 1;
    const periodEndDate = isLastRow ? renderContext.dateRange?.current.end : undefined;

    const name =
      isTimeSeries && typeof dimensionCell.rawValue === 'number'
        ? formatTimeDimensionLabel(dimensionCell.rawValue, interval, renderContext, periodEndDate)
        : dimensionCell.displayValue;

    const color = getSeriesColor(viewBy, String(dimensionCell.rawValue));

    dataPoints.push({
      name,
      y: extractNumericValue(valueCell, fractionUnit),
      ...(color && { color }),
    });
  }

  return dataPoints;
}

function extractNumericValue(cell: Cell | undefined, fractionUnit?: FractionUnit): number | null {
  if (cell === null || cell === undefined) {
    return null;
  }

  if (typeof cell === 'number') {
    return cell;
  }

  if (isDualQueryCell(cell)) {
    return applyFractionUnit(cell, fractionUnit);
  }

  return null;
}

function emptyOptions(): Highcharts.Options {
  return {
    chart: { type: 'pie' },
    colors: SERIES_COLOR_PALETTE,
    plotOptions: {
      pie: {
        innerSize: '60%',
        borderWidth: 0,
        borderRadius: 0,
      },
    },
    series: [],
    legend: { enabled: false },
    credits: { enabled: false },
  };
}
